import { classNames } from '@/util/tailwindHelpers'
import React, { useEffect, useRef, useState } from 'react'
import { useOnClickOutside } from '@/hooks/useOnClickOutside'
import Button, { ButtonLink } from '../Button'
import { ContextualMenuProps } from './types'
import ContextualMenuItemContent from './ContextualMenuItemContent'
import {
  contexutalMenuClasses,
  getPositioningCoords,
  getPositioningString
} from './util'
import { debounce } from '@/util/functionHelpers'

const DEFAULT_APPEARANCE: ContextualMenuProps['appearance'] = {
  anchor: { x: 'left', y: 'bottom' },
  items: {
    align: 'left',
    hoverTheme: 'background',
    separator: false,
    className: ''
  }
}

export default function ContextualMenu({
  className,
  isOpen,
  items,
  buttonRef,
  appearance = DEFAULT_APPEARANCE,
  onClose
}: ContextualMenuProps) {
  const { items: itemConfig, anchor } = appearance
  const [currentAnchor, setCurrentAnchor] = useState(anchor)
  const anchorRef = useRef(currentAnchor)
  const menuRef = useRef<HTMLUListElement>()

  const handleAction = (action, keepContextMenuOpen) => {
    // if the option doesn't explicity request that the context menu
    // stays open, go ahead and auto-close the context menu
    if (!keepContextMenuOpen) {
      onClose()
    }

    // wait just a moment before triggering the action
    // to make sure the close animation has been triggered
    // for actions that open new tabs, it's possible to
    // return to the page and the context menu dismisses
    // at that point
    setTimeout(action, 100)
  }

  const updateContextMenuPosition = () => {
    if (!buttonRef.current || !menuRef.current || !anchorRef.current) {
      return
    }

    const position = getPositioningCoords(
      anchor,
      buttonRef.current.getBoundingClientRect(),
      menuRef.current.getBoundingClientRect()
    )

    if (
      position.x !== anchorRef.current.x ||
      position.y !== anchorRef.current.y
    ) {
      anchorRef.current = position
      setCurrentAnchor(position)
    }
  }
  const debouncedUpdateContextMenuPosition = debounce(
    updateContextMenuPosition,
    300
  )

  useOnClickOutside([menuRef, buttonRef], () => onClose())

  useEffect(() => {
    // set position on mount and viewport resize
    updateContextMenuPosition()

    const handleResize = () => {
      debouncedUpdateContextMenuPosition()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const position = getPositioningString(currentAnchor)

  return (
    <ul
      ref={menuRef}
      className={classNames(
        'bg-gray-850 rounded-3xl shadow-popover opacity-0 pointer-events-none scale-95 transition-all absolute z-modal ease-out',
        contexutalMenuClasses({ position }),
        className,
        {
          ['scale-100 pointer-events-auto opacity-100 translate-y-0 ease-in']:
            isOpen
        }
      )}
    >
      {items.map((item, i) => (
        <li
          key={`${i} - ${item.label}`}
          className={classNames(
            'group text-left [&>*]:last:pb-[10px] [&>*]:first:pt-[10px]',
            itemConfig?.className
          )}
        >
          {/* Link or Button */}
          {item.href ? (
            <ButtonLink size="lg" theme="clean" href={item.href}>
              <ContextualMenuItemContent {...item} {...itemConfig} />
            </ButtonLink>
          ) : (
            <Button
              size="lg"
              theme="clean"
              className="w-full"
              onClick={() =>
                handleAction(item.onClick, item.keepContextMenuOpen)
              }
            >
              <ContextualMenuItemContent {...item} {...itemConfig} />
            </Button>
          )}
        </li>
      ))}
    </ul>
  )
}
